/**
 * DecisionFlow — SSO Callback Handler
 * ui/src/auth/SsoCallback.jsx
 * data-fit LLC · S280
 *
 * Handles the OAuth2 authorization code callback from Google or Microsoft.
 *
 * Flow:
 *   1. Google/Microsoft redirects to /auth/callback?code=...&state=...
 *   2. This component extracts code + state from URL params
 *   3. POSTs to POST /api/v1/auth/sso/callback — server validates state,
 *      exchanges code, validates ID token, sets httpOnly session cookie
 *   4. Calls GET /api/v1/auth/me — hydrates user profile from cookie
 *   5. Calls loginWithSession(user) — sets AuthContext user state
 *   6. Navigates to correct shell via SHELL_ROOT
 *
 * Error states:
 *   - Missing code/state params → redirect to /login with error
 *   - Backend SSO_STATE_INVALID → redirect to /login with error
 *   - Backend SSO_USER_NOT_FOUND → redirect to /login with error
 *   - Backend SSO_DOMAIN_MISMATCH → redirect to /login with error
 *   - Network / unexpected error → redirect to /login with generic error
 *
 * Route: /auth/callback (public — no RouteGuard)
 * Registered in App.jsx alongside /login and /verify-email.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext.jsx";
import { apiPost, apiMe } from "../api/client.js";

// ── Shell redirect map — mirrors Login.jsx ──────────────────────────────────
const SHELL_ROOT = {
  comp_admin:         "/admin/",
  finance:            "/finance/",
  manager:            "/manager/",
  rep:                "/rep/",
  tenant_super_admin: "/tenant-admin/",
  tool_admin:         "/tenant-admin/",
  platform_admin:     "/tenant-admin/",
};

// ── SSO error code → human message map ─────────────────────────────────────
function messageFromCode(code) {
  switch (code) {
    case "SSO_STATE_INVALID":
      return "SSO session expired or is invalid. Please try signing in again.";
    case "SSO_USER_NOT_FOUND":
      return "No DecisionFlow account found for this identity. Contact your administrator.";
    case "SSO_DOMAIN_MISMATCH":
      return "Your email domain is not authorised for this organisation. Contact your administrator.";
    case "USER_ACCOUNT_INACTIVE":
      return "Your account is inactive. Contact your administrator.";
    case "USER_ACCOUNT_LOCKED":
      return "Your account is locked. Contact support@data-fit.com.";
    case "SSO_NOT_CONFIGURED":
      return "SSO is not configured for this organisation. Contact your administrator.";
    default:
      return "SSO sign-in failed. Please try again or contact support@data-fit.com.";
  }
}

export default function SsoCallback() {
  const [searchParams]          = useSearchParams();
  const navigate                = useNavigate();
  const { loginWithSession }    = useAuth();
  const [statusMessage, setStatusMessage] = useState("Completing sign-in…");

  useEffect(() => {
    let cancelled = false;

    async function handleCallback() {
      const code  = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      // IdP-level error — user denied consent or IdP returned an error.
      if (error) {
        const desc = searchParams.get("error_description") || error;
        navigate("/login", {
          replace: true,
          state: { ssoError: `Sign-in was not completed: ${desc}` },
        });
        return;
      }

      // Missing params — malformed callback URL.
      if (!code || !state) {
        navigate("/login", {
          replace: true,
          state: { ssoError: "Invalid sign-in callback. Please try again." },
        });
        return;
      }

      // Deduplication guard — OAuth codes are single-use. If this state token
      // has already been submitted by a parallel mount (React Strict Mode or
      // duplicate navigation), skip the POST and check if the session already
      // landed via /auth/me instead.
      const dedupKey = `df-sso-submitted-${state}`;
      const alreadySubmitted = sessionStorage.getItem(dedupKey) === "1";

      try {
        setStatusMessage("Verifying with your identity provider…");

        if (!alreadySubmitted) {
          sessionStorage.setItem(dedupKey, "1");
          await apiPost("/api/v1/auth/sso/callback", { code, state });
        }

        if (cancelled) return;
        setStatusMessage("Loading your workspace…");

        // Step 4: hydrate user profile from cookie — works whether this instance
        // did the POST or the parallel one did.
        const meResponse = await apiMe();
        if (cancelled) return;

        const userProfile = meResponse?.user ?? meResponse;

        if (!userProfile?.user_type) {
          throw new Error("Invalid user profile returned from /auth/me");
        }

        // Step 5: set AuthContext user state.
        await loginWithSession(userProfile);
        if (cancelled) return;

        // Step 6: navigate to correct shell.
        const shellRoot = SHELL_ROOT[userProfile.user_type] ?? "/login";
        navigate(shellRoot, { replace: true });

      } catch (e) {
        if (cancelled) return;
        console.error("SsoCallback catch:", e);  // ADD THIS LINE
        // Safety net: if the POST failed (e.g. stale code on parallel mount)
        // but a valid session cookie already exists, navigate to the shell.
        try {
          const meResponse = await apiMe();
          const userProfile = meResponse?.user ?? meResponse;
          if (userProfile?.user_type) {
            await loginWithSession(userProfile);
            const shellRoot = SHELL_ROOT[userProfile.user_type] ?? "/login";
            navigate(shellRoot, { replace: true });
            return;
          }
        } catch (_) {
          // /auth/me also failed — genuine error, fall through to error display.
        }

        const errCode = e?.code || e?.error?.code || null;
        const message = messageFromCode(errCode);
        navigate("/login", {
          replace: true,
          state: { ssoError: message },
        });
      }
    }

    handleCallback();
    return () => { cancelled = true; };
  }, []); // Run once on mount — searchParams are stable at callback time.

  // ── Loading UI ─────────────────────────────────────────────────────────────
  // Minimal — matches the right-panel chrome of Login.jsx.
  // Full-screen so there's no flash of unstyled content during redirect.
  return (
    <div style={{
      display:         "flex",
      alignItems:      "center",
      justifyContent:  "center",
      height:          "100vh",
      flexDirection:   "column",
      gap:             "16px",
      fontFamily:      '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
      background:      "rgb(var(--color-surface, 255 255 255))",
      color:           "rgb(var(--color-text, 15 23 42))",
    }}>
      {/* Spinner */}
      <div style={{
        width:        "32px",
        height:       "32px",
        border:       "3px solid rgb(var(--color-border, 226 232 240))",
        borderTop:    "3px solid rgb(var(--color-brand-primary, 10 36 99))",
        borderRadius: "50%",
        animation:    "df-spin 0.8s linear infinite",
      }} />
      <style>{`
        @keyframes df-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Status message */}
      <div style={{
        fontSize:   "var(--font-size-sm, 0.875rem)",
        color:      "rgb(var(--color-text-secondary, 100 116 139))",
        fontWeight: 500,
      }}>
        {statusMessage}
      </div>

      {/* Product identity — low emphasis */}
      <div style={{
        position:   "absolute",
        bottom:     "24px",
        fontSize:   "var(--font-size-xs, 0.75rem)",
        color:      "rgb(var(--color-text-tertiary, 148 163 184))",
        letterSpacing: "0.01em",
      }}>
        data-fit LLC · DecisionFlow
      </div>
    </div>
  );
}
